import type { DiveObject, Locale, SpotDetail, SuggestionCreate } from "@divespots/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ObjectFields } from "../admin/ObjectFields.tsx";
import { SpotFields } from "../admin/SpotFields.tsx";
import {
  emptyObjectForm,
  emptySpotForm,
  isFormDirty,
  issuesToErrors,
  objectToForm,
  spotToForm,
} from "../admin/forms.ts";
import type { FieldErrors, Issue, ObjectForm, SpotForm } from "../admin/forms.ts";
import { DiscardSheet, useUnsavedGuard } from "../admin/unsaved.tsx";
import { Field, Section, errorAt } from "../admin/ui.tsx";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { useSpot } from "../api/index.ts";
import { Button } from "../components/Button.tsx";
import { useLocale } from "../i18n/index.ts";
import {
  validateObjectEditSuggestion,
  validateObjectSuggestion,
  validateSpotEditSuggestion,
  validateSpotSuggestion,
} from "../suggest/payloads.ts";

/** One submit pipeline for all four kinds: validation errors on the fields, 429 and the rest as a banner. */
export function useSuggestionForm(dirty: boolean) {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const guard = useUnsavedGuard(dirty);

  const send = useMutation({
    // the decision mail is written in the language the diver submitted in
    mutationFn: (body: SuggestionCreate) => api.createSuggestion({ ...body, locale }),
    onSuccess: async () => {
      guard.allowLeave();
      await qc.invalidateQueries({ queryKey: ["suggestions"] });
      void navigate(`/${locale}/konto/vorschlaege`, { state: { thanks: true } });
    },
    onError: (e) => {
      if (isApiRequestError(e)) {
        if (e.code === "rate_limited") return setBanner(t("suggest.err.tooMany"));
        if (e.code === "bad_request" && Array.isArray(e.details)) {
          const mapped = (e.details as Issue[]).map((i) => ({
            ...i,
            path: i.path[0] === "payload" ? i.path.slice(1) : i.path,
          }));
          setBanner(t("admin.err.fix"));
          return setErrors(issuesToErrors(mapped, t, true));
        }
        if (e.code === "not_found") return setBanner(t("suggest.err.gone"));
        if (e.code === "forbidden" || e.code === "unauthorized")
          return setBanner(t("admin.err.auth"));
      }
      setBanner(t("admin.err.save"));
    },
  });

  /** `v` is the validated body or the field errors to show. */
  const submit = (v: { ok: true; data: SuggestionCreate } | { ok: false; errors: FieldErrors }) => {
    setBanner(null);
    if (!v.ok) {
      setErrors(v.errors);
      setBanner(v.errors._ ?? t("admin.err.fix"));
      return;
    }
    setErrors({});
    send.mutate(v.data);
  };
  return { errors, setErrors, banner, submit, pending: send.isPending, guard };
}

function Frame({
  title,
  lead,
  backTo,
  children,
}: {
  title: string;
  lead: string;
  backTo?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-6">
      <div className="adm-pagehead">
        <h1 className="ds-text-title-1 m-0">{title}</h1>
        {backTo ? (
          <Link className="ds-btn ds-btn--plain" to={backTo}>
            {t("admin.toMap")}
          </Link>
        ) : null}
      </div>
      <p className="ds-text-subheadline text-label-secondary m-0">{lead}</p>
      {children}
    </div>
  );
}

function NoteField({
  value,
  onChange,
  required,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  required: boolean;
  error: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <Section title={t("suggest.noteTitle")}>
      <Field
        label={required ? `${t("suggest.note")} *` : t("suggest.note")}
        hint={required ? t("suggest.noteHintEdit") : t("suggest.noteHint")}
        error={error}
      >
        {({ id, describedBy, invalid }) => (
          <textarea
            id={id}
            className="adm-input adm-textarea"
            rows={3}
            maxLength={1000}
            value={value}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </Field>
    </Section>
  );
}

function Actions({
  pending,
  banner,
  label,
}: {
  pending: boolean;
  banner: string | null;
  label: string;
}) {
  return (
    <div className="adm-actions adm-actions--sticky">
      {banner ? (
        <p className="adm-error m-0" role="alert">
          {banner}
        </p>
      ) : null}
      <Button type="submit" variant="filled" disabled={pending}>
        {label}
      </Button>
    </div>
  );
}

function Loading({ error }: { error: boolean }) {
  const { t } = useTranslation();
  return error ? (
    <p className="adm-error" role="alert">
      {t("suggest.err.gone")}
    </p>
  ) : (
    <p role="status">{t("common.loading")}</p>
  );
}

// ------------------------------------------------------------------ new spot

/** `/konto/vorschlagen/spot`: a new dive spot. No slug, no status: the team decides and the server derives. */
export function SuggestSpotPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [form, setForm] = useState<SpotForm>(emptySpotForm);
  const [note, setNote] = useState("");
  const [textLocale, setTextLocale] = useState<Locale>("de");
  const dirty = isFormDirty(form, emptySpotForm()) || note !== "";
  const f = useSuggestionForm(dirty);
  const set = <K extends keyof SpotForm>(k: K, v: SpotForm[K]) => {
    setForm((x) => ({ ...x, [k]: v }));
    f.setErrors((e) => ({ ...e, [k]: "" }));
  };
  return (
    <Frame title={t("suggest.spotTitle")} lead={t("suggest.lead")} backTo={`/${locale}/`}>
      <form
        className="grid gap-6"
        noValidate
        aria-label={t("suggest.spotTitle")}
        onSubmit={(e) => {
          e.preventDefault();
          f.submit(validateSpotSuggestion(form, note, t));
        }}
      >
        <SpotFields
          mode="diver"
          form={form}
          errors={f.errors}
          set={set}
          setForm={setForm}
          setErrors={f.setErrors}
          textLocale={textLocale}
          setTextLocale={setTextLocale}
        />
        <NoteField value={note} onChange={setNote} required={false} error={f.errors.note} />
        <Actions pending={f.pending} banner={f.banner} label={t("suggest.send")} />
      </form>
      <DiscardSheet blocker={f.guard.blocker} />
    </Frame>
  );
}

// ------------------------------------------------------------------ edit a spot

export function EditSpotSuggestionPage() {
  const { slug } = useParams();
  const spot = useSpot(slug);
  if (!spot.data) return <Loading error={spot.isError} />;
  return <EditSpotForm spot={spot.data} />;
}

function EditSpotForm({ spot }: { spot: SpotDetail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const baseline = useMemo(() => spotToForm(spot), [spot]);
  const [form, setForm] = useState<SpotForm>(baseline);
  const [note, setNote] = useState("");
  const [textLocale, setTextLocale] = useState<Locale>("de");
  const f = useSuggestionForm(isFormDirty(form, baseline) || note !== "");
  const set = <K extends keyof SpotForm>(k: K, v: SpotForm[K]) => {
    setForm((x) => ({ ...x, [k]: v }));
    f.setErrors((e) => ({ ...e, [k]: "" }));
  };
  return (
    <Frame
      title={t("suggest.editSpotTitle", { name: spot.name })}
      lead={t("suggest.editLead")}
      backTo={`/${locale}/spot/${spot.slug}`}
    >
      <form
        className="grid gap-6"
        noValidate
        aria-label={t("suggest.editSpotTitle", { name: spot.name })}
        onSubmit={(e) => {
          e.preventDefault();
          f.submit(validateSpotEditSuggestion(spot, form, note, t));
        }}
      >
        <SpotFields
          mode="diver"
          form={form}
          errors={f.errors}
          set={set}
          setForm={setForm}
          setErrors={f.setErrors}
          textLocale={textLocale}
          setTextLocale={setTextLocale}
        />
        <NoteField value={note} onChange={setNote} required error={f.errors.note} />
        <Actions pending={f.pending} banner={f.banner} label={t("suggest.send")} />
      </form>
      <DiscardSheet blocker={f.guard.blocker} />
    </Frame>
  );
}

// ------------------------------------------------------------------ new object

/** `/konto/vorschlagen/objekt/:slug?lat=&lon=`: a new object on a spot; the map picker shows the computed depth. */
export function SuggestObjectPage() {
  const { slug } = useParams();
  const spot = useSpot(slug);
  if (!spot.data) return <Loading error={spot.isError} />;
  return <SuggestObjectForm spot={spot.data} />;
}

function SuggestObjectForm({ spot }: { spot: SpotDetail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [params] = useSearchParams();
  const at = useMemo(() => {
    const lat = Number(params.get("lat"));
    const lon = Number(params.get("lon"));
    return params.get("lat") && params.get("lon") && Number.isFinite(lat) && Number.isFinite(lon)
      ? { lat, lon }
      : spot.location;
  }, [params, spot.location]);
  const baseline = useMemo(() => emptyObjectForm(at), [at]);
  const [form, setForm] = useState<ObjectForm>(baseline);
  const [note, setNote] = useState("");
  const [textLocale, setTextLocale] = useState<Locale>("de");
  const f = useSuggestionForm(isFormDirty(form, baseline) || note !== "");
  const set = <K extends keyof ObjectForm>(k: K, v: ObjectForm[K]) => {
    setForm((x) => ({ ...x, [k]: v }));
    f.setErrors((e) => ({ ...e, [k]: "" }));
  };
  return (
    <Frame
      title={t("suggest.objectTitle", { name: spot.name })}
      lead={t("suggest.lead")}
      backTo={`/${locale}/spot/${spot.slug}`}
    >
      <form
        className="grid gap-6"
        noValidate
        aria-label={t("suggest.objectTitle", { name: spot.name })}
        onSubmit={(e) => {
          e.preventDefault();
          f.submit(validateObjectSuggestion(spot.id, form, note, t));
        }}
      >
        <Section title={t("admin.objects.addTitle")}>
          <ObjectFields
            mode="diver"
            form={form}
            errors={f.errors}
            set={set}
            setForm={setForm}
            setErrors={f.setErrors}
            locale={textLocale}
            setLocale={setTextLocale}
            spotLocation={spot.location}
          />
        </Section>
        <NoteField
          value={note}
          onChange={setNote}
          required={false}
          error={errorAt(f.errors, "note")}
        />
        <Actions pending={f.pending} banner={f.banner} label={t("suggest.send")} />
      </form>
      <DiscardSheet blocker={f.guard.blocker} />
    </Frame>
  );
}

// ------------------------------------------------------------------ edit an object

export function EditObjectSuggestionPage() {
  const { slug, objectId } = useParams();
  const spot = useSpot(slug);
  if (!spot.data) return <Loading error={spot.isError} />;
  const object = spot.data.objects.find((o) => o.id === objectId);
  if (!object) return <Loading error />;
  return <EditObjectForm spot={spot.data} object={object} />;
}

function EditObjectForm({ spot, object }: { spot: SpotDetail; object: DiveObject }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const baseline = useMemo(() => objectToForm(object), [object]);
  const [form, setForm] = useState<ObjectForm>(baseline);
  const [note, setNote] = useState("");
  const [textLocale, setTextLocale] = useState<Locale>("de");
  const f = useSuggestionForm(isFormDirty(form, baseline) || note !== "");
  const set = <K extends keyof ObjectForm>(k: K, v: ObjectForm[K]) => {
    setForm((x) => ({ ...x, [k]: v }));
    f.setErrors((e) => ({ ...e, [k]: "" }));
  };
  return (
    <Frame
      title={t("suggest.editObjectTitle", { name: spot.name })}
      lead={t("suggest.editLead")}
      backTo={`/${locale}/spot/${spot.slug}`}
    >
      <form
        className="grid gap-6"
        noValidate
        aria-label={t("suggest.editObjectTitle", { name: spot.name })}
        onSubmit={(e) => {
          e.preventDefault();
          f.submit(validateObjectEditSuggestion(object, form, note, t));
        }}
      >
        <Section title={t("admin.objects.editTitle")}>
          <ObjectFields
            mode="diver"
            form={form}
            errors={f.errors}
            set={set}
            setForm={setForm}
            setErrors={f.setErrors}
            locale={textLocale}
            setLocale={setTextLocale}
            spotLocation={spot.location}
          />
        </Section>
        <NoteField value={note} onChange={setNote} required error={f.errors.note} />
        <Actions pending={f.pending} banner={f.banner} label={t("suggest.send")} />
      </form>
      <DiscardSheet blocker={f.guard.blocker} />
    </Frame>
  );
}
